import { Endpoint } from 'Endpoint';
import { SmallButton } from 'components/SmallButton/SmallButton';
import { getClientUrl, makeUrl } from 'core/utils/utils';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { authIsoCodeSelector, authStoreUrlSelector } from 'state/auth/selectors';
import { ReactComponent as ExternalLinkIcon } from 'assets/icons/main-16/external-link-o--16.svg';
import { Tooltip, makeStyles } from '@material-ui/core';
import { RoutePath } from 'RoutePath';
import { useAction } from 'core/store/store';
import {
  makeSnackbarCreateAction,
  makeSnackbarErrorAction,
} from 'state/snackbar/actions';
import i18n from '../../../i18n';
import { SnackbarMessageType } from 'state/snackbar/reducer';

type StyleProps = {
  withOptions?: boolean;
  isLoading: boolean;
};
const useStyles = makeStyles(theme => ({
  wrapper: {
    display: 'flex',
    alignItems: 'stretch',
  },
  button: {
    width: 'auto !important',
    display: 'inline-flex',
    alignItems: 'center',
    height: 'auto !important',
    textAlign: 'center',
    borderTopRightRadius: (props: StyleProps) => (props.withOptions ? 0 : 100),
    borderBottomRightRadius: (props: StyleProps) =>
      props.withOptions ? 0 : 100,
    '& > span': {
      marginRight: (props: StyleProps) =>
        props.withOptions ? 0 : theme.spacing(1),
    },
  },
  viewSiteWrapper: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    backgroundColor: '#2697FF',
    borderRadius: 100,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    pointerEvents: (props: StyleProps) => (props.isLoading ? 'none' : 'auto'),
    opacity: (props: StyleProps) => (props.isLoading ? 0.6 : 1),
    '&:hover': {
      backgroundColor: '#1b64a7',
    },
  },
  viewSite: {
    marginLeft: theme.spacing(1.5),
    marginRight: theme.spacing(2),
  },
  tooltip: {
    fontSize: 14,
  },
}));

interface IPublishButtonProps {
  withOptions?: boolean;
}

export const PublishButton: React.FC<IPublishButtonProps> = ({
  withOptions = false,
}) => {
  const { t } = i18n.useTranslation();
  const storeCustomUrl = useSelector(authStoreUrlSelector);
  const storeIsoCode = useSelector(authIsoCodeSelector);
  const action = useAction();

  const [isLoading, setIsLoading] = useState(false);
  const handlePublish = async () => {
    setIsLoading(true);

    const path = makeUrl(Endpoint.REVALIDATE_STORE, {
      customUrl: storeCustomUrl,
    });
    const revalidateUrl = new URL(path, getClientUrl(storeIsoCode));

    try {
      await fetch(revalidateUrl, { method: 'POST', mode: 'no-cors', body: '' });
      action(
        makeSnackbarCreateAction,
        SnackbarMessageType.SUCCESS_PUBLISH_SITE,
        {},
      );
    } catch (e) {
      action(makeSnackbarErrorAction, 'common:errors.error');
    } finally {
      setIsLoading(false);
    }
  };
  const handleViewPublishedSite = () => {
    const storeUrl = makeUrl(RoutePath.STORE, {
      storeSlug: storeCustomUrl,
    });
    const viewUrl = new URL(storeUrl, getClientUrl(storeIsoCode));
    window.open(viewUrl, '_blank');
  };

  const classes = useStyles({ withOptions, isLoading });

  return (
    <div className={classes.wrapper}>
      <SmallButton
        disabled={isLoading}
        text={'onboarding:settings.publishSite'}
        className={classes.button}
        color={'#2697FF'}
        hoverColor={'#1b64a7'}
        onClick={handlePublish}
      ></SmallButton>
      {withOptions && (
        <Tooltip
          title={t('onboarding:settings.viewPublishedSite')}
          classes={{ tooltip: classes.tooltip }}
        >
          <div
            className={classes.viewSiteWrapper}
            onClick={handleViewPublishedSite}
          >
            <ExternalLinkIcon fill="white" className={classes.viewSite} />
          </div>
        </Tooltip>
      )}
    </div>
  );
};
